import { useAxiosInstances } from '../axios-instance'
import {
  IFaturamentoConfiguracao,
  INotaFiscalModelo,
  IFaturamentoConfiguracaoCompleta,
  IFaturamentoConfiguracaoSistema,
  IDescricaoPadraoNotaFiscal,
  ISubtitulosDisponiveis,
  IDataPadraoVencimento,
  IInserirFaturamentoConfiguracaoRequest,
  IAtualizarFaturamentoConfiguracaoRequest,
  IObterFaturamentoConfiguracaoPorClienteRequest,
  IObterFaturamentoCompletoPorClienteRequest
} from '../../application/models'

export const useFaturamentoApi = () => {
  const { axiosInstance, axiosAuthInstance } = useAxiosInstances()

  // Configuração de timeout para evitar travamentos
  const TIMEOUT = 60000 // 10 segundos

  const obterFaturamentoConfiguracaoPorCliente = async (data: IObterFaturamentoConfiguracaoPorClienteRequest): Promise<IFaturamentoConfiguracao | null> => {
    try {
      const response = await axiosInstance.get(`/faturamento/configuracao/${data.IdCliente}`, {
        timeout: TIMEOUT,
        validateStatus: (status) => status === 200 || status === 204 || status === 404 // Aceita 204/404 (cliente sem configuração)
      })
      
      // Se status 204 ou 404, o cliente não tem configuração de faturamento (comportamento válido)
      if (response.status === 204 || response.status === 404 || !response.data) {
        return null;
      }
      
      return response.data
    } catch (error: any) {
      // Se for 400, pode ser que o cliente não tenha configuração (comportamento legado)
      if (error.response?.status === 400) {
        return null;
      }
      throw error
    }
  }

  const inserirFaturamentoConfiguracao = async (data: IInserirFaturamentoConfiguracaoRequest): Promise<IFaturamentoConfiguracao> => {
    try {
      const response = await axiosAuthInstance.post('/faturamento/configuracao', data, {
        timeout: TIMEOUT
      })
      return response.data
    } catch (error) {
      throw error
    }
  }

  const atualizarFaturamentoConfiguracao = async (data: IAtualizarFaturamentoConfiguracaoRequest): Promise<IFaturamentoConfiguracao> => {
    try {
      const response = await axiosAuthInstance.put(`/faturamento/configuracao/${data.IdFaturamentoConfiguracaoNotaFiscal}`, data, {
        timeout: TIMEOUT
      })
      return response.data
    } catch (error) {
      throw error
    }
  }

  const obterModelosNotaFiscal = async (): Promise<INotaFiscalModelo[]> => {
    try {
      const response = await axiosInstance.get('/faturamento/modelos', {
        timeout: TIMEOUT,
        validateStatus: (status) => status === 200 || status === 400
      })

      if (response.status === 400) {
        return [];
      }

      const result = response.data || [];
      return result;
    } catch (error: any) {
      return [];
    }
  }

  const obterFaturamentoCompletoPorCliente = async (data: IObterFaturamentoCompletoPorClienteRequest): Promise<IFaturamentoConfiguracaoCompleta[]> => {
    try {
      const params = new URLSearchParams()
      if (data.Mes) params.append('mes', data.Mes.toString())
      if (data.Ano) params.append('ano', data.Ano.toString())

      const response = await axiosInstance.get(`/faturamento/completo/${data.IdCliente}?${params.toString()}`, {
        timeout: TIMEOUT
      })
      
      return response.data || []
    } catch (error) {
      return []
    }
  }


  const obterConfiguracoesSistema = async (): Promise<IFaturamentoConfiguracaoSistema> => {
    try {
      const response = await axiosInstance.get('/faturamento/configuracoes-sistema', {
        timeout: TIMEOUT,
        validateStatus: (status) => status === 200 || status === 400
      })
      
      if (response.status === 400) {
        throw new Error(response.data?.message || 'Erro ao obter configurações do sistema');
      }
      
      return response.data
    } catch (error) {
      throw error
    }
  }

  const obterDescricaoPadraoNotaFiscal = async (): Promise<IDescricaoPadraoNotaFiscal> => {
    try {
      const response = await axiosInstance.get('/faturamento/descricao-padrao-nota-fiscal', {
        timeout: TIMEOUT,
        validateStatus: (status) => status === 200 || status === 400
      })
      
      if (response.status === 400) {
        throw new Error(response.data?.message || 'Erro ao obter descrição padrão');
      }
      
      return response.data
    } catch (error) {
      throw error
    }
  }

  const obterSubtitulosDisponiveis = async (): Promise<ISubtitulosDisponiveis> => {
    try {
      const response = await axiosInstance.get('/faturamento/subtitulos-disponiveis', {
        timeout: TIMEOUT,
        validateStatus: (status) => status === 200 || status === 400
      })
      
      if (response.status === 400) {
        throw new Error(response.data?.message || 'Erro ao obter subtítulos');
      }
      
      return response.data
    } catch (error) {
      throw error
    }
  }

  const obterDataPadraoVencimento = async (): Promise<IDataPadraoVencimento> => {
    try {
      const response = await axiosInstance.get('/faturamento/data-padrao-vencimento', {
        timeout: TIMEOUT,
        validateStatus: (status) => status === 200 || status === 400
      })
      
      if (response.status === 400) {
        throw new Error(response.data?.message || 'Erro ao obter data padrão de vencimento');
      }
      
      return response.data
    } catch (error) {
      throw error
    }
  }

  // NOVA FUNÇÃO: Salvar configuração de nota fiscal
  const salvarNotaFiscal = async (data: {
    IdCliente: number,
    Ativo: boolean,
    DiaVencimento: number,
    SimplesNacional: boolean,
    Nome: string,
    NotaFiscalModeloDescricaoPersonalizado: string
  }): Promise<any> => {
    try {
      const response = await axiosAuthInstance.post('/faturamento/configuracao', {
        IdCliente: data.IdCliente,
        Ativo: data.Ativo,
        DiaVencimento: data.DiaVencimento,
        SimplesNacional: data.SimplesNacional,
        IdNotaFiscalModeloDescricao: data.Nome,
        NotaFiscalModeloDescricaoPersonalizado: data.NotaFiscalModeloDescricaoPersonalizado,
        Inclusao: new Date(),
        Atualizacao: new Date()
      }, {
        timeout: TIMEOUT
      })
      return response.data
    } catch (error) {
      throw error
    }
  }

  // NOVA FUNÇÃO: Atualizar configuração de nota fiscal
  const atualizarNotaFiscal = async (data: {
    IdCliente: number,
    Ativo: boolean,
    DiaVencimento: number,
    SimplesNacional: boolean,
    Nome: string,
    NotaFiscalModeloDescricaoPersonalizado: string
  }): Promise<any> => {
    try {
      // Primeiro buscar a configuração existente para obter o ID
      const configExistente = await obterFaturamentoConfiguracaoPorCliente({ IdCliente: data.IdCliente })

      if (!configExistente) {
        // Se não existir, criar nova
        return await salvarNotaFiscal(data)
      }

      // Se existir, atualizar
      const response = await axiosAuthInstance.put(`/faturamento/configuracao/${configExistente.IdFaturamentoConfiguracaoNotaFiscal}`, {
        IdCliente: data.IdCliente,
        Ativo: data.Ativo,
        DiaVencimento: data.DiaVencimento,
        SimplesNacional: data.SimplesNacional,
        IdNotaFiscalModeloDescricao: data.Nome,
        NotaFiscalModeloDescricaoPersonalizado: data.NotaFiscalModeloDescricaoPersonalizado,
        Atualizacao: new Date()
      }, {
        timeout: TIMEOUT
      })
      return response.data
    } catch (error) {
      throw error
    }
  }

  return {
    obterFaturamentoConfiguracaoPorCliente,
    inserirFaturamentoConfiguracao,
    atualizarFaturamentoConfiguracao,
    obterModelosNotaFiscal,
    obterFaturamentoCompletoPorCliente,
    // Novos métodos
    obterConfiguracoesSistema,
    obterDescricaoPadraoNotaFiscal,
    obterSubtitulosDisponiveis,
    obterDataPadraoVencimento,
    // NOVAS FUNÇÕES PARA NOTA FISCAL
    salvarNotaFiscal,     
    atualizarNotaFiscal   
  }
}
